import { i18n } from '@apps/main/modules/localization/localizationSetup';

import type {
  FullStars,
  NeighborhoodPolygonsOnlyHotel,
  StarsRatingFilter,
} from '@libs/trip-planner-interfaces';

const { t } = i18n.global;

export const enum HotelFilter {
  HotelName = 'hotelName',
  RuleFreeCancellation = 'rules.freeCancellation',
  AccommodationTypeHotel = 'typeOfAccommodation.hotel',
  AccommodationTypeApartment = 'typeOfAccommodation.apartment',
  AccommodationTypeHouseOrCottage = 'typeOfAccommodation.houseOrCottage',
  RatingStars = 'rating.stars',
  MealsNoMeals = 'meals.noMeals',
  MealsBreakfast = 'meals.breakfast',
  MealsHalfBoard = 'meals.halfBoard',
  MealsFullBoard = 'meals.fullBoard',
  MealsAllInclusive = 'meals.allInclusive',
  AmenitiesPool = 'amenities.pool',
  AmenitiesParking = 'amenities.parking',
  AmenitiesSpa = 'amenities.spa',
  AmenitiesGym = 'amenities.gym',
  AmenitiesWifi = 'amenities.wifi',
  AmenitiesAirConditioned = 'amenities.airConditioned',
  AmenitiesVehicleChargingStation = 'amenities.vehicleChargingStation',
  ExperienceBusiness = 'experience.business',
  ExperienceFamily = 'experience.family',
  ExperiencePetsAllowed = 'experience.petsAllowed',
  ExperienceGreenSustainableProperty = 'experience.greenSustainableProperty',
  NeighborhoodPolygons = 'neighborhoodPolygons',
}

export type HotelFilterPillConfig =
  | {
      type: HotelFilter.NeighborhoodPolygons;
      textFactory: (
        filters: HotelFiltersForm,
        neighborhoodPolygons: NeighborhoodPolygonsOnlyHotel[]
      ) => NeighborhoodPillData[];
    }
  | {
      type: Exclude<HotelFilter, HotelFilter.NeighborhoodPolygons>;
      textFactory: (filters: HotelFiltersForm) => string;
    };

// Base pill interface
interface BasePillData {
  type: HotelFilter;
  text: string;
}

// Neighborhood pills require id
export interface NeighborhoodPillData extends BasePillData {
  type: HotelFilter.NeighborhoodPolygons;
  id: string;
}

// All other pills cannot have id
export interface OtherPillData extends BasePillData {
  type: Exclude<HotelFilter, HotelFilter.NeighborhoodPolygons>;
  id?: never;
}

// Union of all pills
export type HotelFilterPillData = NeighborhoodPillData | OtherPillData;

export interface HotelNameFilterUpdate {
  type: HotelFilter.HotelName;
  value: string;
}

export interface AccommodationTypeHotelFilterUpdate {
  type: HotelFilter.AccommodationTypeHotel;
  value: boolean;
}

export interface AccommodationTypeApartmentFilterUpdate {
  type: HotelFilter.AccommodationTypeApartment;
  value: boolean;
}

export interface AccommodationTypeHouseOrCottageFilterUpdate {
  type: HotelFilter.AccommodationTypeHouseOrCottage;
  value: boolean;
}

export interface RatingStarFilterUpdate {
  type: HotelFilter.RatingStars;
  value: StarsRatingFilter;
}

export interface MealsNoMealsFilterUpdate {
  type: HotelFilter.MealsNoMeals;
  value: boolean;
}

export interface MealsBreakfastFilterUpdate {
  type: HotelFilter.MealsBreakfast;
  value: boolean;
}

export interface MealsHalfBoardFilterUpdate {
  type: HotelFilter.MealsHalfBoard;
  value: boolean;
}

export interface MealsFullBoardFilterUpdate {
  type: HotelFilter.MealsFullBoard;
  value: boolean;
}

export interface MealsAllInclusiveFilterUpdate {
  type: HotelFilter.MealsAllInclusive;
  value: boolean;
}

export interface AmenitiesPoolFilterUpdate {
  type: HotelFilter.AmenitiesPool;
  value: boolean;
}

export interface AmenitiesSpaFilterUpdate {
  type: HotelFilter.AmenitiesSpa;
  value: boolean;
}

export interface AmenitiesGymFilterUpdate {
  type: HotelFilter.AmenitiesGym;
  value: boolean;
}

export interface AmenitiesWifiFilterUpdate {
  type: HotelFilter.AmenitiesWifi;
  value: boolean;
}

export interface AmenitiesAirConditionedFilterUpdate {
  type: HotelFilter.AmenitiesAirConditioned;
  value: boolean;
}

export interface AmenitiesVehicleChargingStationFilterUpdate {
  type: HotelFilter.AmenitiesVehicleChargingStation;
  value: boolean;
}

export interface AmenitiesParkingFilterUpdate {
  type: HotelFilter.AmenitiesParking;
  value: boolean;
}

export interface NeighborhoodPolygonsFilterUpdate {
  type: HotelFilter.NeighborhoodPolygons;
  value: string;
}

export type AmenityFilterType =
  | HotelFilter.AmenitiesWifi
  | HotelFilter.AmenitiesGym
  | HotelFilter.AmenitiesAirConditioned
  | HotelFilter.AmenitiesSpa
  | HotelFilter.AmenitiesPool
  | HotelFilter.AmenitiesParking
  | HotelFilter.AmenitiesVehicleChargingStation;
export interface AmenityData {
  type: AmenityFilterType;
  formProperty:
    | 'wifi'
    | 'gym'
    | 'airConditioned'
    | 'spa'
    | 'pool'
    | 'parking'
    | 'vehicleChargingStation';
}

export type HotelFilterMealsUpdate =
  | MealsNoMealsFilterUpdate
  | MealsBreakfastFilterUpdate
  | MealsHalfBoardFilterUpdate
  | MealsFullBoardFilterUpdate
  | MealsAllInclusiveFilterUpdate;

// Any new non-boolean HotelFilter must be added to this exclusion list.
export type BooleanHotelFilter = Exclude<
  HotelFilter,
  | HotelFilter.HotelName
  | HotelFilter.RatingStars
  | HotelFilter.NeighborhoodPolygons
>;

export interface BooleanHotelFilterUpdate {
  type: BooleanHotelFilter;
  value: boolean;
}

export type HotelFilterUpdate =
  | HotelNameFilterUpdate
  | RatingStarFilterUpdate
  | NeighborhoodPolygonsFilterUpdate
  | BooleanHotelFilterUpdate;

export type PossibleMealFilters =
  | HotelFilter.MealsNoMeals
  | HotelFilter.MealsBreakfast
  | HotelFilter.MealsHalfBoard
  | HotelFilter.MealsFullBoard
  | HotelFilter.MealsAllInclusive;

export interface RatingFilterSection {
  stars: FullStars[];
}

export interface RulesFilterSection {
  freeCancellation: boolean;
}

export interface TypeOfAccommodationFilterSection {
  hotel: boolean;
  apartment: boolean;
  houseOrCottage: boolean;
}

export interface MealsFilterSection {
  noMeals: boolean;
  breakfast: boolean;
  halfBoard: boolean;
  fullBoard: boolean;
  allInclusive: boolean;
}

export interface AmenitiesFiltersSection {
  pool: boolean;
  parking: boolean;
  spa: boolean;
  gym: boolean;
  wifi: boolean;
  airConditioned: boolean;
  vehicleChargingStation: boolean;
}

export interface ExpetienceFiltersSection {
  business: boolean;
  petsAllowed: boolean;
  family: boolean;
  greenSustainableProperty: boolean;
}

export interface HotelFiltersForm {
  hotelName: string;
  rules: RulesFilterSection;
  typeOfAccommodation: TypeOfAccommodationFilterSection;
  rating: RatingFilterSection;
  meals: MealsFilterSection;
  amenities: AmenitiesFiltersSection;
  experience: ExpetienceFiltersSection;
  neighborhoodPolygons: string[];
}

export const ALL_HOTEL_FILTERS = [
  HotelFilter.HotelName,
  HotelFilter.RuleFreeCancellation,
  HotelFilter.AccommodationTypeHotel,
  HotelFilter.AccommodationTypeApartment,
  HotelFilter.AccommodationTypeHouseOrCottage,
  HotelFilter.RatingStars,
  HotelFilter.MealsNoMeals,
  HotelFilter.MealsBreakfast,
  HotelFilter.MealsHalfBoard,
  HotelFilter.MealsFullBoard,
  HotelFilter.MealsAllInclusive,
  HotelFilter.AmenitiesPool,
  HotelFilter.AmenitiesSpa,
  HotelFilter.AmenitiesGym,
  HotelFilter.AmenitiesWifi,
  HotelFilter.AmenitiesAirConditioned,
  HotelFilter.AmenitiesVehicleChargingStation,
  HotelFilter.AmenitiesParking,
  HotelFilter.ExperienceBusiness,
  HotelFilter.ExperienceFamily,
  HotelFilter.ExperiencePetsAllowed,
  HotelFilter.ExperienceGreenSustainableProperty,
  HotelFilter.NeighborhoodPolygons,
];

export const HOTEL_FILTER_PILLS_CONFIG: Record<
  HotelFilter,
  HotelFilterPillConfig
> = {
  [HotelFilter.HotelName]: {
    type: HotelFilter.HotelName,
    textFactory: () => t('filter.filter_by_name'),
  },
  [HotelFilter.RuleFreeCancellation]: {
    type: HotelFilter.RuleFreeCancellation,
    textFactory: () => t('filter.rules.free_cancellation'),
  },
  [HotelFilter.AccommodationTypeHotel]: {
    type: HotelFilter.AccommodationTypeHotel,
    textFactory: () => t('filter.hotelTypes.hotels'),
  },
  [HotelFilter.AccommodationTypeApartment]: {
    type: HotelFilter.AccommodationTypeApartment,
    textFactory: () => t('filter.hotelTypes.apartments'),
  },
  [HotelFilter.AccommodationTypeHouseOrCottage]: {
    type: HotelFilter.AccommodationTypeHouseOrCottage,
    textFactory: () => t('filter.hotelTypes.housesAndCottages'),
  },
  [HotelFilter.RatingStars]: {
    type: HotelFilter.RatingStars,
    textFactory: (filters: HotelFiltersForm) => {
      const first = filters.rating.stars[0];
      const last = filters.rating.stars[filters.rating.stars.length - 1];

      if (first !== last) {
        return `${t('filter.rating')}: ${first} - ${last}`;
      }

      return `${t('filter.rating')}: ${first}`;
    },
  },
  [HotelFilter.MealsNoMeals]: {
    type: HotelFilter.MealsNoMeals,
    textFactory: () => t('filter.boardBasis.no_meals'),
  },
  [HotelFilter.MealsBreakfast]: {
    type: HotelFilter.MealsBreakfast,
    textFactory: () => t('filter.boardBasis.breakfast'),
  },
  [HotelFilter.MealsHalfBoard]: {
    type: HotelFilter.MealsHalfBoard,
    textFactory: () => t('filter.boardBasis.half_board'),
  },
  [HotelFilter.MealsFullBoard]: {
    type: HotelFilter.MealsFullBoard,
    textFactory: () => t('filter.boardBasis.full_board'),
  },
  [HotelFilter.MealsAllInclusive]: {
    type: HotelFilter.MealsAllInclusive,
    textFactory: () => t('filter.boardBasis.all_inclusive'),
  },
  [HotelFilter.AmenitiesPool]: {
    type: HotelFilter.AmenitiesPool,
    textFactory: () => t('filter.amenities.pool'),
  },
  [HotelFilter.AmenitiesParking]: {
    type: HotelFilter.AmenitiesParking,
    textFactory: () => t('filter.amenities.parking'),
  },
  [HotelFilter.AmenitiesSpa]: {
    type: HotelFilter.AmenitiesSpa,
    textFactory: () => t('filter.amenities.spa'),
  },
  [HotelFilter.AmenitiesGym]: {
    type: HotelFilter.AmenitiesGym,
    textFactory: () => t('filter.amenities.gym'),
  },
  [HotelFilter.AmenitiesWifi]: {
    type: HotelFilter.AmenitiesWifi,
    textFactory: () => t('filter.amenities.wifi'),
  },
  [HotelFilter.AmenitiesAirConditioned]: {
    type: HotelFilter.AmenitiesAirConditioned,
    textFactory: () => t('filter.amenities.air_conditioned'),
  },
  [HotelFilter.AmenitiesVehicleChargingStation]: {
    type: HotelFilter.AmenitiesVehicleChargingStation,
    textFactory: () => t('filter.amenities.electric_vehicle_charging_station'),
  },
  [HotelFilter.ExperienceBusiness]: {
    type: HotelFilter.ExperienceBusiness,
    textFactory: () => t('filter.travel_experience.business'),
  },
  [HotelFilter.ExperienceFamily]: {
    type: HotelFilter.ExperienceFamily,
    textFactory: () => t('filter.travel_experience.family'),
  },
  [HotelFilter.ExperiencePetsAllowed]: {
    type: HotelFilter.ExperiencePetsAllowed,
    textFactory: () => t('filter.travel_experience.pets'),
  },
  [HotelFilter.ExperienceGreenSustainableProperty]: {
    type: HotelFilter.ExperienceGreenSustainableProperty,
    textFactory: () => t('filter.travel_experience.sustainable_property'),
  },
  [HotelFilter.NeighborhoodPolygons]: {
    type: HotelFilter.NeighborhoodPolygons,
    textFactory: (
      form: HotelFiltersForm,
      neighborhoodPolygons: NeighborhoodPolygonsOnlyHotel[]
    ) => {
      const selected = form.neighborhoodPolygons || [];
      return selected.map((polygonId) => {
        const polygonName = neighborhoodPolygons.find(
          (neighborhood) => neighborhood.id === polygonId
        )?.name;
        return {
          type: HotelFilter.NeighborhoodPolygons,
          text: polygonName ?? t('filter.neighbourhood'),
          id: polygonId,
        };
      });
    },
  },
};

export const DEFAULT_HOTEL_FILTERS: HotelFiltersForm = {
  hotelName: '',
  rules: { freeCancellation: false },
  typeOfAccommodation: {
    hotel: false,
    apartment: false,
    houseOrCottage: false,
  },
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  rating: { stars: [1, 2, 3, 4, 5] },
  meals: {
    noMeals: false,
    breakfast: false,
    halfBoard: false,
    fullBoard: false,
    allInclusive: false,
  },
  amenities: {
    pool: false,
    parking: false,
    spa: false,
    gym: false,
    wifi: false,
    airConditioned: false,
    vehicleChargingStation: false,
  },
  experience: {
    business: false,
    petsAllowed: false,
    family: false,
    greenSustainableProperty: false,
  },
  neighborhoodPolygons: [],
};

export const DEFAULT_HOTEL_FILTER_UPDATE: Record<
  HotelFilter,
  HotelFilterUpdate
> = {
  [HotelFilter.HotelName]: {
    type: HotelFilter.HotelName,
    value: '',
  },
  [HotelFilter.RuleFreeCancellation]: {
    type: HotelFilter.RuleFreeCancellation,
    value: false,
  },
  [HotelFilter.AccommodationTypeHotel]: {
    type: HotelFilter.AccommodationTypeHotel,
    value: false,
  },
  [HotelFilter.AccommodationTypeApartment]: {
    type: HotelFilter.AccommodationTypeApartment,
    value: false,
  },
  [HotelFilter.AccommodationTypeHouseOrCottage]: {
    type: HotelFilter.AccommodationTypeHouseOrCottage,
    value: false,
  },
  [HotelFilter.RatingStars]: {
    type: HotelFilter.RatingStars,
    value: 0,
  },
  [HotelFilter.MealsNoMeals]: {
    type: HotelFilter.MealsNoMeals,
    value: false,
  },
  [HotelFilter.MealsBreakfast]: {
    type: HotelFilter.MealsBreakfast,
    value: false,
  },
  [HotelFilter.MealsHalfBoard]: {
    type: HotelFilter.MealsHalfBoard,
    value: false,
  },
  [HotelFilter.MealsFullBoard]: {
    type: HotelFilter.MealsFullBoard,
    value: false,
  },
  [HotelFilter.MealsAllInclusive]: {
    type: HotelFilter.MealsAllInclusive,
    value: false,
  },
  [HotelFilter.AmenitiesPool]: {
    type: HotelFilter.AmenitiesPool,
    value: false,
  },
  [HotelFilter.AmenitiesParking]: {
    type: HotelFilter.AmenitiesParking,
    value: false,
  },
  [HotelFilter.AmenitiesSpa]: {
    type: HotelFilter.AmenitiesSpa,
    value: false,
  },
  [HotelFilter.AmenitiesGym]: {
    type: HotelFilter.AmenitiesGym,
    value: false,
  },
  [HotelFilter.AmenitiesWifi]: {
    type: HotelFilter.AmenitiesWifi,
    value: false,
  },
  [HotelFilter.AmenitiesAirConditioned]: {
    type: HotelFilter.AmenitiesAirConditioned,
    value: false,
  },
  [HotelFilter.AmenitiesVehicleChargingStation]: {
    type: HotelFilter.AmenitiesVehicleChargingStation,
    value: false,
  },
  [HotelFilter.ExperienceBusiness]: {
    type: HotelFilter.ExperienceBusiness,
    value: false,
  },
  [HotelFilter.ExperienceFamily]: {
    type: HotelFilter.ExperienceFamily,
    value: false,
  },
  [HotelFilter.ExperiencePetsAllowed]: {
    type: HotelFilter.ExperiencePetsAllowed,
    value: false,
  },
  [HotelFilter.ExperienceGreenSustainableProperty]: {
    type: HotelFilter.ExperienceGreenSustainableProperty,
    value: false,
  },
  [HotelFilter.NeighborhoodPolygons]: {
    type: HotelFilter.NeighborhoodPolygons,
    value: '',
  },
};
